// Blocco azioni Sentra del pannello Invio Pratiche (estratto move-only, Fase 5).
import { Send, XCircle, Lock } from "lucide-react";
import type { SentraPrefill, A2AStatus } from "@/types/admin";

export interface PannelloAzioniSentraProps {
  prefill: SentraPrefill;
  status: A2AStatus | null;
  sending: boolean; annulling: boolean; jobRunning: boolean;
  sentraLogin: { step: "idle" | "starting" | "otp" | "working" | "error"; loginId?: string; oppId?: string; msg?: string };
  sentraOtp: string; setSentraOtp: (v: string) => void;
  setConfirm: (c: { msg: string; danger?: boolean; onYes: () => void } | null) => void;
  onLoginStart: () => void;
  onLoginOtpSubmit: () => void;
  onInvia: () => void;
  onAnnulla: () => void;
}

export function PannelloAzioniSentra(props: PannelloAzioniSentraProps) {
  const { prefill, status, sending, annulling, jobRunning, sentraLogin, sentraOtp, setSentraOtp, setConfirm, onLoginStart, onLoginOtpSubmit, onInvia, onAnnulla } = props;
    // Sentra: single-shot (nessuna bozza/step separato, nessun modulo firmato
    // gestito da Plan B). invia_sms è SEMPRE false: nessun controllo di invio
    // SMS/email al cliente esposto qui — il contatto resta manuale su Akyra.
    const loginBusy = sentraLogin.step === "starting" || sentraLogin.step === "otp" || sentraLogin.step === "working";
    const busy = sending || annulling || jobRunning || loginBusy;
    const canSend = prefill.supportato && prefill.has_documento && !busy;
    // job Sentra fallito per sessione scaduta → propone login inline invece del
    // generico "Errore: ...". Il login NON contatta il cliente (solo re-auth).
    const sessionExpired = status?.stato === "errore" && !!status.errore?.includes("sessione_scaduta");
    return (
      <div className="space-y-3">
        {sessionExpired && (
          <div className="rounded-lg px-3 py-2.5 bg-amber-50 border border-amber-300 text-sm text-amber-900 space-y-2">
            <p className="font-medium">Sessione Sentra scaduta — fai login</p>
            {(sentraLogin.step === "idle" || sentraLogin.step === "error") && (
              <>
                {sentraLogin.step === "error" && sentraLogin.msg && (
                  <p className="text-xs text-red-700">{sentraLogin.msg}</p>
                )}
                <button
                  type="button"
                  onClick={onLoginStart}
                  className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors cursor-pointer font-medium"
                >
                  Login
                </button>
              </>
            )}
            {sentraLogin.step === "starting" && (
              <p className="text-xs flex items-center gap-2">
                <span className="h-3.5 w-3.5 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
                Avvio login…
              </p>
            )}
            {sentraLogin.step === "otp" && (
              <div className="space-y-2">
                <p className="text-xs text-amber-900">
                  Inserisci il codice OTP ricevuto via SMS sull'utenza Sentra (numero registrato, non il tuo).
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={sentraOtp}
                    onChange={(e) => setSentraOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="h-9 w-28 px-2.5 text-sm rounded-lg border border-input bg-transparent tracking-widest font-mono text-center"
                  />
                  <button
                    type="button"
                    disabled={sentraOtp.length !== 6}
                    onClick={onLoginOtpSubmit}
                    className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-45 disabled:cursor-not-allowed cursor-pointer font-medium"
                  >
                    Conferma
                  </button>
                </div>
              </div>
            )}
            {sentraLogin.step === "working" && (
              <p className="text-xs flex items-center gap-2">
                <span className="h-3.5 w-3.5 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
                Verifica OTP…
              </p>
            )}
          </div>
        )}

        <div className="space-y-1">
          <button
            type="button"
            disabled={!canSend}
            onClick={() => setConfirm({
              danger: false,
              msg: "Verrà generato il contratto Sentra e il link di firma. NESSUN SMS/email parte al cliente da qui (invio manuale su Akyra). Confermi?",
              onYes: () => { setConfirm(null); onInvia(); },
            })}
            className="w-full px-4 py-2.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-45 disabled:cursor-not-allowed font-medium cursor-pointer flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            <span className="flex flex-col items-center leading-tight">
              <span>{jobRunning ? "Invio in corso…" : (sending ? "Avvio…" : "Invia a Sentra")}</span>
              <span className="text-[11px] font-normal opacity-70">contratto + link firma · nessun invio automatico</span>
            </span>
          </button>
          {!prefill.has_documento && (
            <p className="text-[11px] text-amber-700 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Carica il documento identità su EspoCRM per abilitare l'invio.
            </p>
          )}
        </div>

        {prefill.contratto_id && (
          <div className="pt-2 border-t border-border">
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm({
                danger: true,
                msg: "Azzera lo stato di invio Sentra su CRM. La pratica su Akyra NON viene cancellata: farlo a mano sul portale Sentra. Confermi?",
                onYes: () => { setConfirm(null); onAnnulla(); },
              })}
              className="w-full px-4 py-2 text-sm border border-destructive/50 text-destructive rounded-lg hover:bg-destructive/10 transition-colors disabled:opacity-50 font-medium cursor-pointer flex items-center justify-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              {annulling ? "Annullamento…" : "Annulla (reset locale)"}
            </button>
          </div>
        )}
      </div>
    );
}
